import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    movements: [],

    summary: {
        movementCount: 0,
        saleMovementCount: 0,
        reversalMovementCount: 0,
        totalQuantitySold: 0,
        totalQuantityRestored: 0,
        netQuantityOut: 0,
        isStockReversed: false,
    },

    invoiceId: "",

    includeReversals: true,

    loading: false,

    error: "",
    retryResult: null,

    retryLoading: false,

    retryError: "",
};

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback
) =>
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.payload?.message ||
    error?.payload ||
    error?.message ||
    fallback;

const getResponseData = (
    response
) =>
    response?.data ||
    response ||
    {};

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

const normalizeSummary = (
    summary = {}
) => ({
    movementCount:
        toNumber(
            summary.movementCount
        ),

    saleMovementCount:
        toNumber(
            summary.saleMovementCount
        ),

    reversalMovementCount:
        toNumber(
            summary.reversalMovementCount
        ),

    totalQuantitySold:
        toNumber(
            summary.totalQuantitySold
        ),

    totalQuantityRestored:
        toNumber(
            summary.totalQuantityRestored
        ),

    netQuantityOut:
        toNumber(
            summary.netQuantityOut
        ),

    isStockReversed:
        Boolean(
            summary.isStockReversed
        ),
});

/*
|--------------------------------------------------------------------------
| FETCH SALE INVOICE STOCK MOVEMENTS
|--------------------------------------------------------------------------
*/

export const fetchSaleInvoiceStockMovements =
    createAsyncThunk(
        "saleInvoiceStock/fetchMovements",

        async (
            {
                invoiceId,
                includeReversals = true,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Sale invoice ID is required."
                    );
                }

                if (
                    typeof config
                        .getSaleInvoiceStockMovements !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Sale invoice stock movement API is not configured."
                    );
                }

                const api =
                    new API();

                const url =
                    `${config.getSaleInvoiceStockMovements(
                        invoiceId
                    )}?includeReversals=${includeReversals
                        ? "true"
                        : "false"
                    }`;

                const response =
                    await api.get(
                        url
                    );

                if (
                    response?.success ===
                    false ||
                    response?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        response?.message ||
                        "Unable to fetch invoice stock movements."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                return {
                    invoiceId:
                        responseData
                            ?.invoiceId ||
                        invoiceId,

                    movements:
                        Array.isArray(
                            responseData
                                ?.movements
                        )
                            ? responseData
                                .movements
                            : [],

                    summary:
                        normalizeSummary(
                            responseData
                                ?.summary
                        ),

                    includeReversals:
                        Boolean(
                            includeReversals
                        ),
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch invoice stock movements."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| RETRY SALE INVOICE STOCK POSTING
|--------------------------------------------------------------------------
*/

export const retrySaleInvoiceStockPosting =
    createAsyncThunk(
        "saleInvoiceStock/retryStockPosting",

        async (
            {
                invoiceId,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Sale invoice ID is required."
                    );
                }

                if (
                    typeof config
                        .retrySaleInvoiceStockPosting !==
                    "function"
                ) {
                    return rejectWithValue(
                        "Retry stock posting API is not configured."
                    );
                }

                const api =
                    new API();

                const response =
                    await api.patch(
                        config.retrySaleInvoiceStockPosting(
                            invoiceId
                        ),
                        {}
                    );

                if (
                    response?.success ===
                    false ||
                    response?.status ===
                    "error"
                ) {
                    return rejectWithValue(
                        response?.message ||
                        "Unable to retry invoice stock posting."
                    );
                }

                const responseData =
                    getResponseData(
                        response
                    );

                return {
                    invoice:
                        responseData
                            ?.invoice ||
                        null,

                    movements:
                        Array.isArray(
                            responseData
                                ?.movements
                        )
                            ? responseData
                                .movements
                            : [],

                    skippedItems:
                        Array.isArray(
                            responseData
                                ?.skippedItems
                        )
                            ? responseData
                                .skippedItems
                            : [],

                    alreadyPosted:
                        Boolean(
                            responseData
                                ?.alreadyPosted
                        ),

                    message:
                        response?.message ||
                        "Invoice stock posted successfully.",
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to retry invoice stock posting."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const saleInvoiceStockSlice =
    createSlice({
        name:
            "saleInvoiceStock",

        initialState,

        reducers: {
            setIncludeSaleInvoiceStockReversals: (
                state,
                action
            ) => {
                state.includeReversals =
                    Boolean(
                        action.payload
                    );
            },

            clearSaleInvoiceStockMovements: (
                state
            ) => {
                state.movements =
                    [];

                state.summary = {
                    ...initialState.summary,
                };

                state.invoiceId =
                    "";

                state.error =
                    "";
            },

            clearSaleInvoiceStockError: (
                state
            ) => {
                state.error =
                    "";
            },

            resetSaleInvoiceStockState:
                () => ({
                    ...initialState,

                    movements:
                        [],

                    summary: {
                        ...initialState.summary,
                    },
                }),

            clearSaleInvoiceStockRetryResult: (
                state
            ) => {
                state.retryResult =
                    null;

                state.retryError =
                    "";
            },

            clearSaleInvoiceStockRetryError: (
                state
            ) => {
                state.retryError =
                    "";
            },
        },

        extraReducers: (
            builder
        ) => {
            builder

                .addCase(
                    fetchSaleInvoiceStockMovements
                        .pending,
                    (
                        state
                    ) => {
                        state.loading =
                            true;

                        state.error =
                            "";
                    }
                )

                .addCase(
                    fetchSaleInvoiceStockMovements
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading =
                            false;

                        state.invoiceId =
                            action.payload
                                ?.invoiceId ||
                            "";

                        state.movements =
                            Array.isArray(
                                action.payload
                                    ?.movements
                            )
                                ? action
                                    .payload
                                    .movements
                                : [];

                        state.summary = {
                            ...initialState.summary,

                            ...action
                                .payload
                                ?.summary,
                        };

                        state.includeReversals =
                            Boolean(
                                action.payload
                                    ?.includeReversals
                            );
                    }
                )

                .addCase(
                    fetchSaleInvoiceStockMovements
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading =
                            false;

                        state.error =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to fetch invoice stock movements.";
                    }
                )
                /*
|--------------------------------------------------------------------------
| RETRY STOCK POSTING
|--------------------------------------------------------------------------
*/

                .addCase(
                    retrySaleInvoiceStockPosting
                        .pending,
                    (
                        state
                    ) => {
                        state.retryLoading =
                            true;

                        state.retryError =
                            "";

                        state.retryResult =
                            null;
                    }
                )

                .addCase(
                    retrySaleInvoiceStockPosting
                        .fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.retryLoading =
                            false;

                        state.retryResult =
                            action.payload ||
                            null;

                        state.retryError =
                            "";

                        const returnedMovements =
                            Array.isArray(
                                action.payload
                                    ?.movements
                            )
                                ? action.payload
                                    .movements
                                : [];

                        if (
                            returnedMovements.length >
                            0
                        ) {
                            state.movements =
                                returnedMovements;
                        }
                    }
                )

                .addCase(
                    retrySaleInvoiceStockPosting
                        .rejected,
                    (
                        state,
                        action
                    ) => {
                        state.retryLoading =
                            false;

                        state.retryError =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to retry invoice stock posting.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setIncludeSaleInvoiceStockReversals,
    clearSaleInvoiceStockMovements,
    clearSaleInvoiceStockError,
    clearSaleInvoiceStockRetryResult,
    clearSaleInvoiceStockRetryError,
    resetSaleInvoiceStockState,
} =
    saleInvoiceStockSlice.actions;

/*
|--------------------------------------------------------------------------
| BASE SELECTOR
|--------------------------------------------------------------------------
*/

export const saleInvoiceStockSelector =
    (
        state
    ) =>
        state
            ?.saleInvoiceStock ||
        initialState;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const saleInvoiceStockMovementsSelector =
    (
        state
    ) =>
        saleInvoiceStockSelector(
            state
        ).movements;

export const saleInvoiceStockSummarySelector =
    (
        state
    ) =>
        saleInvoiceStockSelector(
            state
        ).summary;

export const saleInvoiceStockLoadingSelector =
    (
        state
    ) =>
        Boolean(
            saleInvoiceStockSelector(
                state
            ).loading
        );

export const saleInvoiceStockErrorSelector =
    (
        state
    ) =>
        saleInvoiceStockSelector(
            state
        ).error;

export const saleInvoiceStockIncludeReversalsSelector =
    (
        state
    ) =>
        Boolean(
            saleInvoiceStockSelector(
                state
            ).includeReversals
        );

export const saleInvoiceStockOutMovementsSelector =
    (
        state
    ) =>
        saleInvoiceStockMovementsSelector(
            state
        ).filter(
            (
                movement
            ) =>
                movement
                    ?.movementType ===
                "sale"
        );

export const saleInvoiceStockReversalMovementsSelector =
    (
        state
    ) =>
        saleInvoiceStockMovementsSelector(
            state
        ).filter(
            (
                movement
            ) =>
                movement
                    ?.movementType ===
                "sale_cancelled"
        );

export const saleInvoiceStockRetryLoadingSelector =
    (
        state
    ) =>
        Boolean(
            saleInvoiceStockSelector(
                state
            ).retryLoading
        );

export const saleInvoiceStockRetryErrorSelector =
    (
        state
    ) =>
        saleInvoiceStockSelector(
            state
        ).retryError ||
        "";

export const saleInvoiceStockRetryResultSelector =
    (
        state
    ) =>
        saleInvoiceStockSelector(
            state
        ).retryResult;

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default
    saleInvoiceStockSlice.reducer;